import React from 'react';
import { ShieldCheck, FileText, Check } from 'lucide-react';

interface SecurityScannerAnimationProps {
  fileName?: string;
}

const SecurityScannerAnimation: React.FC<SecurityScannerAnimationProps> = ({ fileName }) => {
  const items = ["Credit Cards", "API Keys", "Passwords", "Emails", "Personally Identifiable Information (PII)"];

  return (
    <div className="flex flex-col items-center w-full max-w-[340px] mx-auto p-6 bg-white rounded-xl shadow-2xl relative overflow-hidden">
      <div className="flex items-center gap-2 mb-6 z-10">
        <ShieldCheck className="text-blue-600 animate-pulse" size={24} />
        <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Security Scan</h3>
      </div>

      <div className="relative w-40 h-56 border border-gray-200 rounded-lg bg-gray-50 mb-6 overflow-hidden flex flex-col items-center justify-center shadow-inner z-10">
        {/* Document Header */}
        <div className="absolute top-4 w-full px-4 flex items-center justify-center gap-2 text-gray-400">
          <FileText size={16} />
          <span className="text-[10px] font-medium truncate max-w-[100px]">{fileName || 'document'}</span>
        </div>
        
        {/* Document Skeleton Lines */}
        <div className="w-24 h-1.5 bg-gray-200 rounded mt-8 mb-2.5"></div>
        <div className="w-16 h-1.5 bg-gray-200 rounded mb-2.5"></div>
        <div className="w-28 h-1.5 bg-gray-200 rounded mb-2.5"></div>
        <div className="w-20 h-1.5 bg-gray-200 rounded"></div>

        {/* Animated Scanner Beam */}
        <div className="absolute top-0 left-0 w-full h-[80px] pointer-events-none scanner-beam-container">
          <style>{`
            .scanner-beam-container {
              animation: scanBeam 2.5s linear infinite;
            }
            .scanner-beam {
              background: linear-gradient(to bottom, transparent, rgba(59, 130, 246, 0.05) 60%, rgba(59, 130, 246, 0.3) 100%);
              box-shadow: 0 1px 8px rgba(59, 130, 246, 0.4);
            }
            @keyframes scanBeam {
              0% { transform: translateY(-100%); opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(224px); opacity: 0; }
            }
            @media (prefers-reduced-motion: reduce) {
              .scanner-beam-container {
                display: none;
              }
              .no-motion-hide {
                animation: none !important;
              }
            }
          `}</style>
          <div className="w-full h-full scanner-beam border-b border-blue-500"></div>
        </div>
      </div>

      <div className="w-full flex flex-col items-start px-2 z-10">
        <p className="text-sm font-medium text-gray-800 mb-3 no-motion-hide animate-pulse">Analyzing document...</p>
        
        <ul className="text-xs text-gray-600 space-y-2 mb-6 w-full">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2 opacity-0 animate-fade-in-seq" style={{ animationDelay: `${(index + 1) * 0.5}s`, animationFillMode: 'forwards' }}>
              <Check size={14} className="text-green-500 flex-shrink-0" />
              <span className="truncate">{item}</span>
            </li>
          ))}
        </ul>
        <style>{`
          @keyframes fadeInSeq {
            to { opacity: 1; transform: translateY(0); }
            from { opacity: 0; transform: translateY(4px); }
          }
          .animate-fade-in-seq {
            animation-name: fadeInSeq;
            animation-duration: 0.4s;
            animation-timing-function: ease-out;
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-in-seq {
              animation: none !important;
              opacity: 1 !important;
              transform: none !important;
            }
          }
        `}</style>

        <p className="text-xs text-gray-500 text-center w-full mt-2">
          Generating Security Report<span className="loading-dots"></span>
        </p>
        <style>{`
          @keyframes loadingDots {
            0% { content: "."; }
            33% { content: ".."; }
            66% { content: "..."; }
            100% { content: ""; }
          }
          .loading-dots::after {
            content: ".";
            animation: loadingDots 1.5s infinite steps(1);
          }
          @media (prefers-reduced-motion: reduce) {
             .loading-dots::after {
                animation: none !important;
                content: "...";
             }
          }
        `}</style>
      </div>
    </div>
  );
};

export default SecurityScannerAnimation;
